/**
 * GraphQL WebSocket Client
 *
 * Singleton WebSocket client for GraphQL subscriptions.
 * SSR-safe: Only initializes in browser environment.
 * Supports auth via setConnectionParamsGetter so subscriptions can send a Bearer token.
 */

import { createClient, Client } from 'graphql-ws';
import { logger } from '@/lib/logger';

let wsClient: Client | null = null;
let isInitializing = false;
let missingUrlWarned = false;

/** Optional getter for connection params (e.g. Bearer token). Set by app before getWsClient() so subscriptions are authenticated. */
let connectionParamsGetter: (() => Record<string, unknown>) | null = null;

/**
 * Set the factory for WebSocket connection params. Call with a function that returns { authorization: `Bearer ${token}` } when the user is authenticated.
 * Read on each handshake. `useUserNotifications` sets it during render and calls `reconnectWsClient` when the bearer identity changes so an already-open socket picks it up.
 */
export function setConnectionParamsGetter(getter: (() => Record<string, unknown>) | null): void {
  connectionParamsGetter = getter;
}

/**
 * Drop the live socket without disposing the graphql-ws client.
 *
 * `connectionParams` is read only when a handshake starts. A later token
 * change is invisible to a socket that is already open, and `closeWsClient`
 * is the wrong follow-up: `dispose` plus `terminate` sets `disposed` and
 * drops the singleton, so sibling subscriptions (`userNotifications`,
 * `savedEventChanged`) stay dead until something subscribes again.
 *
 * Closing with 4499 is graphql-ws's retryable "Terminated" code. The client
 * retries because `shouldRetry` is always true and 4499 is not in its fatal
 * list, then replays every subscription that still holds a lock. The next
 * `connection_init` reads the getter set just before this call.
 */
export function reconnectWsClient(): void {
  if (typeof window === 'undefined' || !wsClient) {
    return;
  }
  const socket = activeSocket;
  if (!socket) {
    return;
  }
  const connecting = typeof WebSocket !== 'undefined' ? WebSocket.CONNECTING : 0;
  const open = typeof WebSocket !== 'undefined' ? WebSocket.OPEN : 1;
  const readyState = socket.readyState;
  if (readyState !== undefined && readyState !== connecting && readyState !== open) {
    return;
  }
  try {
    socket.close(4499, 'Terminated');
  } catch (error) {
    console.error('Failed to reconnect WebSocket socket:', error);
  }
}

/** Tracks actual WebSocket connection state (on.connected / on.closed). */
let connectionStatus = false;
const connectionListeners = new Set<(connected: boolean) => void>();

/**
 * Socket captured from graphql-ws `opened` (fires before ConnectionAck).
 * `dispose()` awaits the connection promise, which never resolves while
 * ack is pending (default `connectionAckWaitTimeout` is 0 = wait forever).
 */
let activeSocket: CloseableSocket | null = null;

interface CloseableSocket {
  readonly readyState?: number;
  close: (code?: number, reason?: string) => void;
}

function isCloseableSocket(value: unknown): value is CloseableSocket {
  return (
    typeof value === 'object' &&
    value !== null &&
    'close' in value &&
    typeof (value as { close: unknown }).close === 'function'
  );
}

function rememberSocket(socket: unknown): void {
  if (isCloseableSocket(socket)) {
    activeSocket = socket;
  }
}

function closeTrackedSocket(): void {
  const socket = activeSocket;
  activeSocket = null;
  if (!socket) return;
  const connecting = typeof WebSocket !== 'undefined' ? WebSocket.CONNECTING : 0;
  const open = typeof WebSocket !== 'undefined' ? WebSocket.OPEN : 1;
  const readyState = socket.readyState;
  if (readyState !== undefined && readyState !== connecting && readyState !== open) {
    return;
  }
  try {
    socket.close(4499, 'Terminated');
  } catch (error) {
    console.error('Failed to close WebSocket socket:', error);
  }
}

function notifyConnectionStatus(connected: boolean): void {
  connectionStatus = connected;
  connectionListeners.forEach((listener) => listener(connected));
}

/**
 * Gets or creates the GraphQL WebSocket client.
 * Returns null during SSR or if initialization fails.
 *
 * @returns WebSocket client instance or null
 */
export function getWsClient(): Client | null {
  // SSR guard: only run in browser
  if (typeof window === 'undefined') {
    return null;
  }

  // WEB-369 / WEB-955: admin is a CMS. Root Providers still mount
  // NotificationToastBridge (the userNotifications WS hook). Opening
  // graphql-ws from `/admin/*` hammers whatever NEXT_PUBLIC_WS_URL is
  // baked in — on staging that has been the prod WS host (ws.loci.city).
  // Dispose a consumer-page singleton here: returning null alone leaves
  // an already-open client (and its subscriptions) alive across client nav.
  if (window.location.pathname.startsWith('/admin')) {
    closeWsClient();
    return null;
  }

  // Return existing client if already initialized
  if (wsClient) {
    return wsClient;
  }

  // Prevent multiple simultaneous initializations
  if (isInitializing) {
    return null;
  }

  try {
    isInitializing = true;

    const wsUrl = process.env.NEXT_PUBLIC_WS_URL;

    if (!wsUrl) {
      // Warn once per session — real-time features are optional and absent env
      // is a configuration state, not a runtime error. Subsequent getWsClient()
      // calls (from useUserNotifications, useSavedEventChanged, etc.) no-op quietly.
      if (!missingUrlWarned) {
        missingUrlWarned = true;
        logger.warn('NEXT_PUBLIC_WS_URL is not configured — real-time subscriptions disabled');
      }
      return null;
    }

    // Create WebSocket client with exponential backoff retry
    wsClient = createClient({
      url: wsUrl,
      retryAttempts: 5,
      shouldRetry: () => true,
      retryWait: (retries) => {
        // Exponential backoff: 1s, 2s, 4s, 8s, 16s
        return new Promise((resolve) => {
          const delay = Math.min(1000 * Math.pow(2, retries), 16000);
          setTimeout(resolve, delay);
        });
      },
      connectionParams: () => {
        const params = connectionParamsGetter?.() ?? {};
        return typeof params === 'object' && params !== null ? params : {};
      },
      on: {
        opened: (socket) => {
          rememberSocket(socket);
        },
        connected: (socket) => {
          rememberSocket(socket);
          logger.debug('WebSocket connected');
          notifyConnectionStatus(true);
        },
        closed: (event) => {
          activeSocket = null;
          logger.debug('WebSocket closed', { event });
          notifyConnectionStatus(false);
        },
        error: (error) => {
          console.error('WebSocket error:', error);
        },
      },
    });

    return wsClient;
  } catch (error) {
    console.error('Failed to create WebSocket client:', error);
    wsClient = null;
    return null;
  } finally {
    isInitializing = false;
  }
}

/**
 * Subscribes to the client's actual WebSocket connection state.
 * State is driven by graphql-ws on.connected / on.closed (not by subscribe()).
 * Call the returned function to unsubscribe.
 *
 * @param callback - Called with current state immediately, then on each change
 * @returns Unsubscribe function
 */
export function subscribeToConnectionStatus(callback: (connected: boolean) => void): () => void {
  connectionListeners.add(callback);
  callback(connectionStatus);
  return () => {
    connectionListeners.delete(callback);
  };
}

/**
 * Closes the WebSocket client connection.
 * Useful for cleanup during component unmount or app shutdown.
 */
export function closeWsClient(): void {
  if (!wsClient) {
    closeTrackedSocket();
    return;
  }
  // Drop the singleton first so a later getWsClient() cannot reuse a
  // disposing client, even if dispose() throws or its Promise rejects
  // (graphql-ws handshake failure). Do not await — admin nav must not wait.
  const client = wsClient;
  wsClient = null;
  try {
    // graphql-ws v5.16.2 `dispose()` awaits the connection promise. With
    // default ack timeout 0 that promise never settles until ConnectionAck,
    // so a client still handshaking would hang forever. Start dispose
    // first (`disposed = true` runs synchronously, blocking retries), then
    // `terminate()` so the pending connection rejects and the socket
    // actually closes.
    const disposal = Promise.resolve(client.dispose());
    if (typeof client.terminate === 'function') {
      client.terminate();
    }
    closeTrackedSocket();
    void disposal.catch((error: unknown) => {
      console.error('Failed to close WebSocket client:', error);
    });
  } catch (error) {
    closeTrackedSocket();
    console.error('Failed to close WebSocket client:', error);
  }
}

/**
 * Type guard to check if client is available
 */
export function isWsClientAvailable(): boolean {
  return typeof window !== 'undefined' && wsClient !== null;
}
